import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { CircleSlash, Copy, Lock, Paperclip, TriangleAlert } from 'lucide-react';
import {
  DISPLAYED_REQUEST_STATUSES,
  ESTIMATES,
  IMPACTS,
  PRIORITIES,
  REQUEST_TYPES,
  type HistoryEntry,
  type QualificationDetail,
  type QualificationUpdate,
} from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { AttachmentChip } from '@/components/portal/attachment-chip';
import { TeamsConcernedBadge } from '@/components/portal/duplicate-badges';
import { ImpactTag } from '@/components/portal/impact-tag';
import { Pill } from '@/components/portal/pill';
import { SelectedFiles } from '@/components/portal/selected-files';
import { StatusPill } from '@/components/portal/status-pill';
import { TypeTag } from '@/components/portal/type-tag';
import { Field, inputClass, NativeSelect } from '@/features/requests/request-form';
import { lookupRequest, useCreateVersion, useJiraEpics, useLinkJira, useLinkMonday, useMarkDuplicate, useQualification, useQualify, useRefuse, useTeamMessage } from '@/hooks/use-product';
import { ApiRequestError, ApiValidationError, isApiError } from '@/lib/api';
import { formatDate, formatDayTime, fullName, shortName } from '@/lib/format';
import {
  formatBusinessDays,
  IMPACT_SHORT_LABELS,
  INTAKE_CHANNEL_LABELS,
  NO_PRODUCT_LABEL,
  PRIORITY_LABELS,
  TYPE_LABELS,
  STATUS_LABELS,
} from '@/lib/labels';
import { filesOf } from '@/lib/files';
import { cn, initials } from '@/lib/utils';
import { NotFoundPage } from '../not-found-page';

const cardClass = 'flex flex-col rounded-card border border-border bg-card';

function errorText(error: unknown): string | null {
  if (error instanceof ApiValidationError) return error.issues.map((i) => i.message).join(' ');
  if (error instanceof ApiRequestError) return error.message;
  return null;
}

function ErrorLine({ error }: { error: unknown }) {
  const text = errorText(error);
  return text ? (
    <p role="alert" className="m-0 text-xs font-bold text-destructive">
      {text}
    </p>
  ) : null;
}

/** « délai de qualification : 1,2 j sur 2 », with its state. Business days, computed by the API. */
function delayText(request: QualificationDetail): string {
  const base = `délai de qualification : ${formatBusinessDays(request.delay.businessDays).replace(' j', '')} j sur 2`;
  if (request.delay.overdue) return `${base} · hors délai`;
  if (request.delay.suspended) return `${base} · suspendu`;
  return base;
}

// ---------------------------------------------------------------------------
// Header actions: refuse, mark as duplicate
// ---------------------------------------------------------------------------

function RefusePanel({ request, onClose }: { request: QualificationDetail; onClose: () => void }) {
  const refuse = useRefuse(request.id);
  const [reason, setReason] = useState('');
  return (
    <section className="flex flex-col gap-3 rounded-card border border-status-refused bg-card px-6 py-5">
      <h2 className="m-0 text-[15px] font-bold text-status-refused-foreground">Refuser la demande</h2>
      <Field id="motif" label="Motif du refus" required hint="Obligatoire. Il est envoyé au demandeur comme message et affiché sur sa demande.">
        <textarea id="motif" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className={cn(inputClass, 'resize-y py-3')} />
      </Field>
      <ErrorLine error={refuse.error} />
      <div className="flex flex-wrap justify-end gap-2.5">
        <Button type="button" variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button type="button" disabled={refuse.isPending || !reason.trim()} onClick={() => refuse.mutate(reason, { onSuccess: onClose })}>
          Refuser et prévenir le demandeur
        </Button>
      </div>
    </section>
  );
}

/**
 * Banner of 08 (ADR-009). « Doublon probable »: suggested on screen 16, the
 * requester answered « C'est différent »; « Doublon possible »: best
 * candidate of the same search, never shown to the requester.
 */
function DuplicateSignalBanner({ request, signal }: { request: QualificationDetail; signal: NonNullable<QualificationDetail['duplicateSignal']> }) {
  const c = signal.candidate;
  return (
    <section className="flex flex-wrap items-center gap-3.5 rounded-card border border-warning-border bg-warning px-5 py-3.5">
      <TriangleAlert aria-hidden="true" className="size-[18px] text-warning-foreground" />
      <span className="flex-[1_1_300px] text-[13px] text-foreground-secondary">
        <strong className="text-warning-foreground">{signal.kind === 'probable' ? 'Doublon probable :' : 'Doublon possible :'}</strong> {c.reference} « {c.title} » (
        {c.teamName}, {STATUS_LABELS[c.status]}).
        {signal.kind === 'probable' && ` Proposé à la soumission ; ${request.requester.firstName} a répondu « c'est différent ».`}
        {signal.commonWords.length > 0 && ` Mots en commun : ${signal.commonWords.join(', ')}.`}
      </span>
      <Link to={`/produit/demandes/${c.id}`} className="text-[13px] font-bold no-underline">
        Comparer
      </Link>
    </section>
  );
}

function DuplicatePanel({ request, onClose }: { request: QualificationDetail; onClose: () => void }) {
  const mark = useMarkDuplicate(request.id);
  const [reference, setReference] = useState(request.duplicateSignal?.candidate.reference ?? '');
  const [found, setFound] = useState<{ id: string; reference: string; title: string; teamName: string } | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const find = async () => {
    setFound(null);
    setLookupError(null);
    try {
      setFound(await lookupRequest(reference));
    } catch (error) {
      setLookupError(errorText(error));
    }
  };
  return (
    <section className="flex flex-col gap-3 rounded-card border border-border bg-card px-6 py-5">
      <h2 className="m-0 text-[15px] font-bold">Marquer comme doublon</h2>
      <p className="m-0 text-[13px] text-foreground-secondary">
        La demande est rattachée à la demande d'origine ; son demandeur et ses abonnés suivent désormais celle-ci. Les notes internes restent internes.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <Field id="origine" label="Demande d'origine" hint="Référence, ex. PP-305.">
          <input id="origine" type="text" value={reference} onChange={(e) => setReference(e.target.value)} className={cn(inputClass, 'min-h-10')} />
        </Field>
        <Button type="button" variant="secondary" className="mb-[22px]" onClick={() => void find()} disabled={!reference.trim()}>
          Rechercher
        </Button>
      </div>
      {lookupError && <p className="m-0 text-xs font-bold text-destructive">{lookupError}</p>}
      {found && (
        <p className="m-0 text-[13px]">
          <strong>{found.reference}</strong> « {found.title} » ({found.teamName})
        </p>
      )}
      <ErrorLine error={mark.error} />
      <div className="flex flex-wrap justify-end gap-2.5">
        <Button type="button" variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button type="button" disabled={!found || mark.isPending} onClick={() => found && mark.mutate(found.id, { onSuccess: onClose })}>
          Rattacher à {found?.reference ?? 'la demande'}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Request, exchanges
// ---------------------------------------------------------------------------

function BusinessRequest({ request }: { request: QualificationDetail }) {
  const facts: [string, ReactNode][] = [];
  if (request.cmsArticleUrl) facts.push(["URL de l'article", <a href={request.cmsArticleUrl} target="_blank" rel="noreferrer noopener" className="break-all font-bold no-underline">{request.cmsArticleUrl.replace(/^https?:\/\//, '')}</a>]);
  if (request.url) facts.push(['URL concernée', <a href={request.url} target="_blank" rel="noreferrer noopener" className="break-all font-bold no-underline">{request.url.replace(/^https?:\/\//, '')}</a>]);
  if (request.clientEmail) facts.push(['Client concerné', <span className="font-bold">{request.clientEmail} · NCCT {request.clientNcct}</span>]);
  facts.push(['Impact déclaré', <ImpactTag impact={request.impactDeclared} />]);
  return (
    <section className={cn(cardClass, 'gap-3.5 px-6 py-5')}>
      <h2 className="m-0 text-[15px] font-bold">Demande du métier</h2>
      <p className="m-0 whitespace-pre-line text-sm leading-relaxed text-body-strong">{request.description}</p>
      <dl className="m-0 grid grid-cols-1 gap-3 rounded-control border border-muted bg-surface-subtle px-4 py-3.5 text-[13px] sm:grid-cols-3">
        {facts.map(([label, value]) => (
          <div key={label} className="flex min-w-0 flex-col gap-1">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="m-0">{value}</dd>
          </div>
        ))}
      </dl>
      {request.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2.5">
          {request.attachments.map((a) => (
            <AttachmentChip key={a.id} attachment={a} href={`/api/product/attachments/${a.id}`} />
          ))}
        </div>
      )}
    </section>
  );
}

const ROLE_SUFFIX = { requester: 'Demandeur', referent: 'Référent', po: 'Équipe produit' } as const;

function Exchanges({ request }: { request: QualificationDetail }) {
  const send = useTeamMessage(request.id);
  const [visibility, setVisibility] = useState<'public' | 'internal'>('public');
  const [body, setBody] = useState('');
  const [expectsReply, setExpectsReply] = useState(true);
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const closed = request.status === 'refused' || request.status === 'duplicate';
  return (
    <section className={cardClass}>
      <div className="border-b border-border px-6 py-4">
        <h2 className="m-0 text-[15px] font-bold">Échanges</h2>
      </div>
      <ol className="m-0 flex list-none flex-col gap-5 px-6 py-5">
        {request.messages.length === 0 && <li className="text-[13px] text-muted-foreground">Aucun échange pour le moment.</li>}
        {request.messages.map((m) => {
          const internal = m.visibility === 'internal';
          return (
            <li key={m.id} className="flex items-start gap-3.5">
              <span
                aria-label={internal ? 'Note interne' : fullName(m.author)}
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                  internal ? 'bg-warning-border text-warning-foreground' : 'bg-system-avatar text-system-avatar-foreground',
                )}
              >
                {internal ? <Lock aria-hidden="true" className="size-3.5" /> : initials(m.author.firstName, m.author.lastName)}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span className="text-[13px] text-muted-foreground">
                  <strong className="font-bold text-foreground">{shortName(m.author)}</strong> · {m.byRequester ? 'Demandeur' : ROLE_SUFFIX[m.authorRole]} ·{' '}
                  {formatDayTime(m.createdAt)}
                  {internal && <span className="font-bold text-warning-foreground"> · Note interne, invisible du métier</span>}
                  {m.expectsReply && <span className="font-bold text-warning-foreground"> · attend une réponse</span>}
                </span>
                <div
                  className={cn(
                    'whitespace-pre-line rounded-card px-3.5 py-3 text-sm leading-[1.55] text-body-strong',
                    internal ? 'border border-dashed border-warning-border bg-warning' : 'bg-bubble',
                  )}
                >
                  {m.body}
                </div>
                {m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2.5">
                    {m.attachments.map((a) => (
                      <AttachmentChip key={a.id} attachment={a} href={`/api/product/attachments/${a.id}`} />
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {!closed && (
        <form
          className="flex flex-col gap-3 border-t border-border px-6 pb-5 pt-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!body.trim()) return;
            send.mutate(
              { input: { body, visibility, expectsReply: visibility === 'public' && expectsReply }, files },
              {
                onSuccess: () => {
                  setBody('');
                  setFiles([]);
                },
              },
            );
          }}
        >
          <fieldset className="m-0 flex flex-wrap gap-1.5 border-0 p-0">
            <legend className="sr-only">Destinataire</legend>
            {(['public', 'internal'] as const).map((v) => (
              <label
                key={v}
                className={cn(
                  'inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-control border px-3 text-[13px]',
                  visibility === v ? 'border-primary bg-accent font-bold text-accent-strong' : 'border-border bg-card text-foreground-secondary',
                )}
              >
                <input type="radio" name="destination" checked={visibility === v} onChange={() => setVisibility(v)} className="m-0 accent-primary" />
                {v === 'public' ? 'Message au demandeur' : 'Note interne'}
              </label>
            ))}
          </fieldset>
          <label htmlFor="message" className="sr-only">
            Message
          </label>
          <textarea
            id="message"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={visibility === 'internal' ? 'Visible de l’équipe produit uniquement.' : 'Visible du demandeur et de ses abonnés.'}
            className={cn(inputClass, 'resize-y py-3 leading-normal', visibility === 'internal' && 'bg-warning')}
          />
          <SelectedFiles files={files} onRemove={(index) => setFiles((current) => current.filter((_, i) => i !== index))} />
          <ErrorLine error={send.error} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {visibility === 'public' && (
                <label className="flex min-h-8 items-center gap-2.5 text-[13px] text-foreground-secondary">
                  <input type="checkbox" checked={expectsReply} onChange={(e) => setExpectsReply(e.target.checked)} className="m-0 size-[18px] accent-primary" />
                  <span>
                    Attendre la réponse du demandeur <span className="text-muted-foreground">· suspend le délai de qualification</span>
                  </span>
                </label>
              )}
              <Button type="button" variant="secondary" size="sm" className="min-h-9 font-normal" onClick={() => fileInput.current?.click()}>
                <Paperclip aria-hidden="true" className="size-4" />
                Joindre
              </Button>
              <input
                ref={fileInput}
                type="file"
                multiple
                className="hidden"
                onChange={(event) => {
                  // Copied before the input is reset (see lib/files).
                  const picked = filesOf(event.target.files);
                  if (picked.length > 0) setFiles((current) => [...current, ...picked]);
                  event.target.value = '';
                }}
              />
            </div>
            <Button type="submit" disabled={send.isPending || !body.trim()}>
              {visibility === 'public' ? 'Envoyer au demandeur' : 'Enregistrer la note'}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Qualification panel
// ---------------------------------------------------------------------------

interface Draft {
  productId: string;
  type: string;
  status: string;
  priority: string;
  impactRetained: string;
  estimate: string;
  targetVersionId: string;
  plannedDeliveryDate: string;
  ownerId: string;
}

const draftOf = (r: QualificationDetail): Draft => ({
  productId: r.product?.id ?? '',
  type: r.type,
  status: r.status,
  priority: r.priority ?? '',
  impactRetained: r.impactRetained ?? '',
  estimate: r.estimate ?? '',
  targetVersionId: r.targetVersion?.id ?? '',
  plannedDeliveryDate: r.plannedDeliveryDate ?? '',
  ownerId: r.owner?.id ?? '',
});

/** Only what changed is sent; '' means « cleared ». */
function changesOf(before: Draft, after: Draft): QualificationUpdate {
  const update: Record<string, unknown> = {};
  for (const key of Object.keys(after) as (keyof Draft)[]) {
    if (after[key] === before[key]) continue;
    if (key === 'productId' || key === 'status' || key === 'type') {
      if (after[key]) update[key] = after[key];
    } else update[key] = after[key] === '' ? null : after[key];
  }
  return update as QualificationUpdate;
}

function PanelField({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-bold">
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * Monday instead of Jira (ADR-015): create the item on the product's board, or
 * assign an existing one by its link. Once linked, the status follows both ways.
 */
function MondayBlock({ request }: { request: QualificationDetail }) {
  const link = useLinkMonday(request.id);
  const [item, setItem] = useState('');
  const canLink = request.product !== null && request.mondayBoard !== null && request.mondayAvailable;
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3.5">
      <span className="text-[13px] font-bold">Item Monday</span>
      {request.monday ? (
        <div className="flex flex-col gap-0.5 rounded-control border border-border bg-surface-subtle px-3 py-2.5 text-[13px]">
          <a href={request.monday.url} target="_blank" rel="noreferrer" className="font-bold">
            Item {request.monday.itemId}
          </a>
          <span className="text-xs text-muted-foreground">
            {request.monday.lastKnownStatus ? `Statut Monday : ${request.monday.lastKnownStatus}` : 'Statut Monday vide'}
            {request.monday.lastSyncedAt && ` · synchronisé le ${formatDayTime(request.monday.lastSyncedAt)}`}
          </span>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2.5 rounded-control border border-border bg-surface-subtle px-3 py-2.5">
            <span className="flex flex-1 flex-col gap-0.5 text-[13px] text-foreground-secondary">
              <span>Aucun item lié</span>
              <span className="text-xs text-muted-foreground">Board du produit : {request.mondayBoard?.name ?? request.mondayBoard?.id}</span>
            </span>
            <Button type="button" variant="outline" size="sm" className="min-h-9" disabled={!canLink || link.isPending} onClick={() => link.mutate({ mode: 'create' })}>
              Créer l'item
            </Button>
          </div>
          <div className="flex gap-2">
            <label htmlFor="monday-existant" className="sr-only">
              Affecter un item existant
            </label>
            <input
              id="monday-existant"
              type="text"
              value={item}
              onChange={(e) => setItem(e.target.value)}
              placeholder="Lien ou numéro de l'item Monday"
              className={cn(inputClass, 'min-h-10 min-w-0 flex-1 text-[13px]')}
              disabled={!canLink}
            />
            <Button type="button" variant="secondary" disabled={!canLink || !item.trim() || link.isPending} onClick={() => link.mutate({ mode: 'assign', itemId: item })}>
              Affecter
            </Button>
          </div>
          {!request.mondayAvailable && <span className="text-xs text-muted-foreground">Monday n'est pas configuré dans cet environnement.</span>}
          <ErrorLine error={link.error} />
        </>
      )}
      <span className="text-xs text-muted-foreground">Une fois lié, le statut de la demande suit l'item dans les deux sens.</span>
    </div>
  );
}

function JiraBlock({ request }: { request: QualificationDetail }) {
  const link = useLinkJira(request.id);
  const [key, setKey] = useState('');
  const [epicKey, setEpicKey] = useState('');
  const canLink = request.product !== null && request.jiraBoard !== null && request.jiraAvailable;
  const epics = useJiraEpics(request.id, canLink && request.jira === null);
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3.5">
      <span className="text-[13px] font-bold">Ticket Jira</span>
      {request.jira ? (
        <div className="flex flex-col gap-0.5 rounded-control border border-border bg-surface-subtle px-3 py-2.5 text-[13px]">
          <span className="font-bold">{request.jira.key}</span>
          <span className="text-xs text-muted-foreground">
            {request.jira.lastKnownStatus ? `Statut Jira : ${request.jira.lastKnownStatus}` : 'Statut Jira inconnu'}
            {request.jira.lastSyncedAt && ` · synchronisé le ${formatDayTime(request.jira.lastSyncedAt)}`}
          </span>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2.5 rounded-control border border-border bg-surface-subtle px-3 py-2.5">
            <span className="flex flex-1 flex-col gap-0.5 text-[13px] text-foreground-secondary">
              <span>Aucun ticket lié</span>
              <span className="text-xs text-muted-foreground">
                {!request.product
                  ? 'Attribuez d’abord un produit : le ticket se crée dans son board.'
                  : request.jiraBoard
                    ? `Board du produit : ${request.product.name} · ${request.jiraBoard.projectKey}`
                    : 'Aucun board Jira lié à ce produit.'}
              </span>
            </span>
            <Button type="button" variant="outline" size="sm" className="min-h-9" disabled={!canLink || link.isPending} onClick={() => link.mutate({ mode: 'create', epicKey: epicKey || null })}>
              Créer le ticket
            </Button>
          </div>
          {/* ADR-013: an existing epic only; the portal never creates one. */}
          <NativeSelect id="jira-epic" aria-label="Épic du ticket créé" value={epicKey} onChange={(e) => setEpicKey(e.target.value)} disabled={!canLink || epics.isPending} className="min-h-10 text-[13px]">
            <option value="">{epics.isPending && canLink ? 'Chargement des épics…' : 'Sans épic'}</option>
            {(epics.data?.items ?? []).map((epic) => (
              <option key={epic.key} value={epic.key}>
                {epic.key} · {epic.summary}
              </option>
            ))}
          </NativeSelect>
          <div className="flex gap-2">
            <label htmlFor="jira-existant" className="sr-only">
              Affecter un ticket existant
            </label>
            <input
              id="jira-existant"
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder={`Clé du ticket, ex. ${request.jiraBoard?.projectKey ?? 'DP'}-1840`}
              className={cn(inputClass, 'min-h-10 min-w-0 flex-1 text-[13px]')}
              disabled={!canLink}
            />
            <Button type="button" variant="secondary" disabled={!canLink || !key.trim() || link.isPending} onClick={() => link.mutate({ mode: 'assign', jiraKey: key })}>
              Affecter
            </Button>
          </div>
          {!request.jiraAvailable && <span className="text-xs text-muted-foreground">Jira n'est pas configuré dans cet environnement.</span>}
          <ErrorLine error={link.error} />
        </>
      )}
      <span className="text-xs text-muted-foreground">Une fois lié, le statut de la demande suit le ticket dans les deux sens.</span>
    </div>
  );
}

const NEW_VERSION = '__new__';

function QualificationPanel({ request }: { request: QualificationDetail }) {
  const qualify = useQualify(request.id);
  const initial = draftOf(request);
  const [draft, setDraft] = useState<Draft>(initial);
  // Server state refreshed (after a save, a Jira sync…): start again from it.
  const serverKey = JSON.stringify(initial);
  useEffect(() => setDraft(JSON.parse(serverKey) as Draft), [serverKey]);
  const set = (key: keyof Draft) => (event: { target: { value: string } }) => setDraft((d) => ({ ...d, [key]: event.target.value }));
  const closed = request.status === 'refused' || request.status === 'duplicate';
  const productChanged = draft.productId !== initial.productId;
  const update = changesOf(initial, draft);
  const dirty = Object.keys(update).length > 0;
  const createVersion = useCreateVersion();
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const addVersion = () => {
    if (!request.product || !newVersion?.trim()) return;
    createVersion.mutate(
      { productId: request.product.id, number: newVersion.trim(), plannedDate: null },
      {
        onSuccess: ({ id }) => {
          setDraft((d) => ({ ...d, targetVersionId: id }));
          setNewVersion(null);
        },
      },
    );
  };

  return (
    <form
      className={cn(cardClass, 'gap-4 p-5')}
      onSubmit={(event) => {
        event.preventDefault();
        if (dirty) qualify.mutate(update);
      }}
    >
      <h2 className="m-0 text-base font-bold">Qualification</h2>
      <fieldset disabled={closed} className="m-0 flex flex-col gap-4 border-0 p-0">
        {/* ADR-003: the product comes first when the request has none; mandatory before « Qualifiée ». */}
        <PanelField id="produit" label={request.product ? 'Produit' : 'Produit à attribuer'}>
          <NativeSelect id="produit" value={draft.productId} onChange={set('productId')} disabled={request.jira !== null} className={cn(!request.product && 'border-warning-border bg-warning')}>
            {!request.product && <option value="">{NO_PRODUCT_LABEL}</option>}
            {request.options.products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </PanelField>
        {/* Corrected by the PO: an « Aide / accès » leaves the backlog, a bug or an evolution in the backlog statuses enters it. */}
        <PanelField id="type" label="Type">
          <NativeSelect id="type" value={draft.type} onChange={set('type')}>
            {REQUEST_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </NativeSelect>
        </PanelField>
        <PanelField id="statut" label="Statut">
          <NativeSelect id="statut" value={draft.status} onChange={set('status')}>
            {DISPLAYED_REQUEST_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </NativeSelect>
        </PanelField>
        <div className="grid grid-cols-2 gap-3">
          <PanelField id="prio" label="Priorité produit">
            <NativeSelect id="prio" value={draft.priority} onChange={set('priority')}>
              <option value="">—</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </NativeSelect>
          </PanelField>
          <PanelField id="impact" label="Impact retenu">
            <NativeSelect id="impact" value={draft.impactRetained} onChange={set('impactRetained')}>
              <option value="">—</option>
              {IMPACTS.map((i) => (
                <option key={i} value={i}>
                  {IMPACT_SHORT_LABELS[i]}
                </option>
              ))}
            </NativeSelect>
          </PanelField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <PanelField id="estim" label="Estimation">
            <NativeSelect id="estim" value={draft.estimate} onChange={set('estimate')}>
              <option value="">—</option>
              {ESTIMATES.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </NativeSelect>
          </PanelField>
          <PanelField id="version" label="Version cible">
            <NativeSelect
              id="version"
              value={draft.targetVersionId}
              onChange={(e) => (e.target.value === NEW_VERSION ? setNewVersion('') : set('targetVersionId')(e))}
              disabled={!request.product || productChanged}
            >
              <option value="">Non définie</option>
              {request.options.versions.map((v) => (
                <option key={v.id} value={v.id}>
                  {request.product?.name} {v.number}
                </option>
              ))}
              <option value={NEW_VERSION}>+ Nouvelle version…</option>
            </NativeSelect>
          </PanelField>
        </div>
        {/* A version created here, as on screen 13 (ADR-012), becomes the target at once. */}
        {newVersion !== null && request.product && (
          <div className="-mt-2 flex flex-col gap-1.5">
            <div className="flex gap-2">
              <input
                aria-label={`Numéro de la nouvelle version de ${request.product.name}`}
                autoFocus
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addVersion();
                  }
                }}
                placeholder="ex. 3.6"
                className={cn(inputClass, 'min-h-9 min-w-0 flex-1 text-[13px]')}
              />
              <Button type="button" variant="secondary" size="sm" disabled={!newVersion.trim() || createVersion.isPending} onClick={addVersion}>
                Créer
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setNewVersion(null)}>
                Annuler
              </Button>
            </div>
            <ErrorLine error={createVersion.error} />
          </div>
        )}
        {productChanged && <span className="-mt-2 text-xs text-muted-foreground">Enregistrez le produit pour choisir une de ses versions.</span>}
        <PanelField id="livraison" label="Date de livraison prévue">
          <input id="livraison" type="date" value={draft.plannedDeliveryDate} onChange={set('plannedDeliveryDate')} className={inputClass} />
        </PanelField>
        <PanelField id="resp" label="Responsable produit">
          <NativeSelect id="resp" value={draft.ownerId} onChange={set('ownerId')}>
            <option value="">Non assigné</option>
            {request.options.owners.map((o) => (
              <option key={o.id} value={o.id}>
                {fullName(o)}
              </option>
            ))}
          </NativeSelect>
        </PanelField>

        {/* ADR-015: a product may have both tools; a request is linked to one of them only. */}
        {request.jira ? (
          <JiraBlock request={request} />
        ) : request.monday ? (
          <MondayBlock request={request} />
        ) : (
          <>
            {(request.jiraBoard || !request.mondayBoard) && <JiraBlock request={request} />}
            {request.mondayBoard && <MondayBlock request={request} />}
          </>
        )}

        <div className="flex flex-col gap-2.5 border-t border-border pt-3.5">
          {/* TODO(OPEN-QUESTION notification-opt-out): mock-up 08 lets the PO untick the notification; nothing documents
              skipping one. Every change is notified: the box is shown ticked and disabled (arbitrage of 2026-10-19). */}
          <label className="flex min-h-8 items-center gap-2.5 text-[13px] text-foreground-secondary">
            <input type="checkbox" checked disabled readOnly className="m-0 size-[18px]" />
            <span>Notifier le demandeur et les abonnés</span>
          </label>
          <ErrorLine error={qualify.error} />
          <Button type="submit" className="min-h-11" disabled={!dirty || qualify.isPending}>
            Enregistrer la qualification
          </Button>
        </div>
      </fieldset>
    </form>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

function historyText(entry: HistoryEntry, request: QualificationDetail): string {
  const after = entry.after as Record<string, unknown> | string | null;
  const value = (v: unknown) => (typeof v === 'string' ? v : '');
  switch (entry.field) {
    case 'created': {
      const created = after as { channel?: keyof typeof INTAKE_CHANNEL_LABELS } | null;
      return created?.channel ? `Saisie pour ${fullName(request.requester)} · ${INTAKE_CHANNEL_LABELS[created.channel].toLowerCase()}` : 'Création';
    }
    case 'status':
      return `Statut → ${STATUS_LABELS[value(after) as keyof typeof STATUS_LABELS] ?? value(after)}`;
    case 'monday_link':
      return `Item Monday ${value((after as { itemId?: unknown } | null)?.itemId)} ${(after as { mode?: string } | null)?.mode === 'created' ? 'créé' : 'affecté'}`;
    case 'type':
      return `Type → ${TYPE_LABELS[value(after) as keyof typeof TYPE_LABELS] ?? value(after)}`;
    case 'product':
      return `Produit → ${value((after as { name?: unknown } | null)?.name) || request.product?.name || ''}`;
    case 'priority':
      return `Priorité → ${PRIORITY_LABELS[value(after) as keyof typeof PRIORITY_LABELS] ?? '—'}`;
    case 'impact_retained':
      return `Impact retenu → ${IMPACT_SHORT_LABELS[value(after) as keyof typeof IMPACT_SHORT_LABELS] ?? '—'}`;
    case 'estimate':
      return `Estimation → ${value(after) || '—'}`;
    case 'target_version': {
      const version = request.options.versions.find((v) => v.id === after);
      return `Version cible → ${version ? `${request.product?.name ?? ''} ${version.number}` : after ? 'modifiée' : 'Non définie'}`;
    }
    case 'planned_delivery_date':
      return `Livraison prévue → ${value(after) ? formatDate(value(after)) : '—'}`;
    case 'owner': {
      const owner = request.options.owners.find((o) => o.id === after);
      return `Responsable → ${owner ? fullName(owner) : 'Non assigné'}`;
    }
    case 'message':
      return (after as { visibility?: string } | null)?.visibility === 'internal' ? 'Note interne' : 'Message public';
    case 'attachment':
      return `Pièce jointe : ${value((after as { fileName?: unknown } | null)?.fileName)}`;
    case 'subscriber':
      return after ? 'Abonnement' : 'Désabonnement';
    case 'refusal':
      return 'Refus motivé';
    case 'duplicate_of':
      return `Rattachée à ${value((after as { reference?: unknown } | null)?.reference) || 'la demande d’origine'}`;
    case 'jira_link':
      return `Ticket Jira ${value((after as { jiraKey?: unknown } | null)?.jiraKey)} ${(after as { mode?: string } | null)?.mode === 'created' ? 'créé' : 'affecté'}`;
    default:
      return entry.field;
  }
}

function History({ request }: { request: QualificationDetail }) {
  return (
    <section className={cn(cardClass, 'gap-2.5 px-5 py-4')}>
      <h2 className="m-0 text-[15px] font-bold">Historique</h2>
      <ul className="m-0 flex list-none flex-col gap-2 p-0 text-[13px] text-foreground-secondary">
        {request.history.map((entry) => (
          <li key={entry.id}>
            <span className="text-muted-foreground">{formatDayTime(entry.createdAt)} ·</span> {historyText(entry, request)} ({entry.author ? shortName(entry.author) : 'Système'})
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/** 08 · Qualifier une demande. Everything here is PO only (API side). */
export function QualifyPage() {
  const { id = '' } = useParams();
  const query = useQualification(id);
  const [panel, setPanel] = useState<'refuse' | 'duplicate' | null>(null);

  if (query.isPending) return <p className="m-0 text-sm text-muted-foreground">Chargement…</p>;
  if (query.isError) {
    if (isApiError(query.error, 404) || isApiError(query.error, 400)) return <NotFoundPage />;
    return <p className="m-0 text-sm text-muted-foreground">La demande n'a pas pu être chargée.</p>;
  }
  const request = query.data;
  const inQualification = request.status === 'submitted' || request.status === 'qualifying';
  const canRefuse = request.status !== 'refused' && request.status !== 'duplicate' && request.status !== 'delivered';

  return (
    <>
      <div className="flex flex-col gap-2.5">
        <Link to="/produit/qualification" className="text-[13px] no-underline">
          ← File de qualification
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[13px] text-muted-foreground tabular-nums">{request.reference}</span>
              <TypeTag type={request.type} />
              <Pill className="bg-muted text-foreground-secondary">{request.product?.name ?? NO_PRODUCT_LABEL}</Pill>
              <StatusPill status={request.status} />
              <TeamsConcernedBadge count={request.teamsConcerned} />
            </div>
            <h1 className="m-0 text-2xl font-black tracking-[-0.01em]">{request.title}</h1>
            <span className={cn('text-[13px]', request.delay.overdue ? 'font-bold text-overdue-foreground' : 'text-muted-foreground')}>
              Soumise le {formatDate(request.submittedAt)} par {fullName(request.requester)} · {request.team.name}
              {inQualification && ` · ${delayText(request)}`}
              {request.awaitingReply && ' · en attente de réponse'}
            </span>
            {request.enteredBy && (
              <span className="text-[13px] text-muted-foreground">
                Saisie par {shortName(request.enteredBy.person)} · {INTAKE_CHANNEL_LABELS[request.enteredBy.channel].toLowerCase()}
              </span>
            )}
          </div>
          {/* Duplicate: in qualification only (ADR-005 §3); refusal: at any stage but delivery (2026-10-23). */}
          {canRefuse && (
            <div className="flex flex-wrap gap-2.5">
              {inQualification && (
                <Button type="button" variant="secondary" className="text-[13px]" onClick={() => setPanel(panel === 'duplicate' ? null : 'duplicate')}>
                  Marquer comme doublon
                </Button>
              )}
              <Button type="button" variant="secondary" className="text-[13px] text-destructive" onClick={() => setPanel(panel === 'refuse' ? null : 'refuse')}>
                Refuser
              </Button>
            </div>
          )}
        </div>
      </div>

      {panel === 'refuse' && <RefusePanel request={request} onClose={() => setPanel(null)} />}
      {panel === 'duplicate' && <DuplicatePanel request={request} onClose={() => setPanel(null)} />}
      {request.status === 'refused' && (
        <section className="flex items-start gap-3 rounded-card border border-status-refused bg-status-refused px-5 py-3.5 text-sm text-status-refused-foreground">
          <CircleSlash aria-hidden="true" className="mt-0.5 size-[18px] shrink-0" />
          <span>
            <strong>Refusée.</strong> {request.refusalReason}
          </span>
        </section>
      )}
      {request.status === 'duplicate' && request.duplicateOf && (
        <section className="flex items-start gap-3 rounded-card border border-border bg-status-duplicate px-5 py-3.5 text-sm text-status-duplicate-foreground">
          <Copy aria-hidden="true" className="mt-0.5 size-[18px] shrink-0" />
          <span>
            <strong>Doublon de </strong>
            <Link to={`/produit/demandes/${request.duplicateOf.id}`} className="font-bold no-underline">
              {request.duplicateOf.reference} « {request.duplicateOf.title} »
            </Link>
          </span>
        </section>
      )}

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-6">
          <BusinessRequest request={request} />
          {request.duplicateSignal && <DuplicateSignalBanner request={request} signal={request.duplicateSignal} />}
          <Exchanges request={request} />
        </div>
        <aside className="flex min-w-0 flex-[1_1_340px] flex-col gap-6">
          <QualificationPanel request={request} />
          <History request={request} />
        </aside>
      </div>
    </>
  );
}
